/* eslint-disable @typescript-eslint/no-explicit-any */

import { Graphics, Container, Text } from "pixi.js";

import { CrawlService } from "../Services/CrawlService";

import type { IStudioWidget } from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

import { getCrawlDesign } from "../../../../data/crawlDesignsCatalog";

import { CrawlDesignRenderer } from "./CrawlDesignRenderer";

export class CrawlWidgetRenderer {
  render(
    widget: IStudioWidget,

    container: Container,

    destination: { x: number; y: number; width: number; height: number },
  ): void {
    if ((container as any)._crawlTicker) {
      (container as any)._crawlTicker();

      (container as any)._crawlTicker = null;
    }

    container.removeChildren();

    const options = (widget as any).options;

    // ── Design-store path ──────────────────────────────────────────────────

    // When `options.designId` resolves to a catalog design, render that design's

    // fixed chrome (bar + tag) + an animated scroll strip via CrawlDesignRenderer

    // and skip the legacy plain-text crawl. The teardown fn is stashed on the

    // container like the legacy ticker so the next render/removal cleans it up.

    const design = getCrawlDesign(options.designId);

    if (design) {
      const teardown = new CrawlDesignRenderer(design).render(
        container,

        destination,

        options.text ?? "",

        options.velocity,
      );

      (container as any)._crawlTicker = teardown;

      return;
    }

    const backgroundColor = options.backgroundColor;

    const textContent = options.text;

    const rawFontSize = parseInt(options.fontSize);

    const scaleFactor = 2.01;

    const fontSize = rawFontSize * scaleFactor;

    const fontFamily = options.fontFamily;

    const color = options.fontColor;

    const velocityOption = options.velocity;

    const bg = new Graphics();

    bg.rect(0, 0, destination.width, destination.height).fill(backgroundColor);

    container.addChild(bg);

    const mask = new Graphics();

    mask.rect(0, 0, destination.width, destination.height).fill(0xffffff);

    container.addChild(mask);

    container.mask = mask;

    const textStyle = {
      fontFamily,

      fontSize,

      fill: color,

      fontWeight: options.bold ? "bold" : "normal",

      fontStyle: options.italic ? "italic" : "normal",
    } as any;

    if (options.strike) textStyle.stroke = { width: 2, color: color };

    const text = new Text({
      text: textContent,

      style: textStyle,
    });

    text.anchor.set(0, 0.5);

    text.position.set(destination.width, destination.height / 2);

    container.addChild(text);

    const crawlService = CrawlService.getInstance();

    const speed =
      CrawlService.SPEEDS[velocityOption as keyof typeof CrawlService.SPEEDS] ||
      CrawlService.SPEEDS.medium;

    const textRatio = CrawlService.computeTextRatio(
      textContent,

      fontFamily,

      options.fontSize,

      !!options.bold,
    );

    const cycleLen = 1 + textRatio;

    const tick = (time: number) => {
      const progress =
        ((time * speed) / CrawlService.REFERENCE_WIDTH) % cycleLen;

      text.x = (1 - progress) * destination.width;
    };

    crawlService.subscribe(tick);

    (container as any)._crawlTicker = () => crawlService.unsubscribe(tick);
  }

  public destroy() {
    // CrawlService cleanup is handled per-container via _crawlTicker
    // No global cleanup needed as each render cleans up its own ticker
  }
}
